"use client";

import { WEB_COLORS } from "@/lib/constants";
import { useGeolocation } from "@/lib/hooks/common.hooks";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import  "./mappls.css";
import { useLazyGetReverseGeocodeMapplsQuery } from "@/lib/redux";

const SECRET_KEY = process.env.NEXT_PUBLIC_MAPPLS_WEB_SDK_KEY;
const url = `https://sdk.mappls.com/map/sdk/web?v=3.0&access_token=${SECRET_KEY}`;
const pluginUrl = `https://sdk.mappls.com/map/sdk/plugins?access_token=${SECRET_KEY}&v=3.0`;

const pluginsList = ["search"]; // Add other plugins as needed

const Fitbounds = [
  [60.0, -2.0], // Southwest (near Maldives, slightly below)
  [100.0, 42.0], // Northeast (Afghanistan top + Myanmar side)
];

const DefaultCenter = { lat: 28.6139, lng: 77.209 };

const ERROR_MESSAGES={
  OUT_OF_INDIA: "Selected location is outside India. Please choose a location within India to continue.",
}

function createMarker(map, { lat, lng }, options = {}, callback) {
  if (!map) return null;

  const marker = new window.mappls.Marker({
    map,
    position: { lat, lng },
    draggable: options?.draggable ?? true,
    icon_url: options?.icon || "https://apis.mappls.com/map_v3/1.png",
    ...options,
  });

  marker.addListener("dragend", function (dragEvent) {
    const cords = dragEvent.target.getPosition();
    callback && callback(cords);
  });

  return marker;
}

const IndiaRegionAreaStrings = [
  "Bay Of Bengal",
  "Arabian Sea",
  "Indian Ocean",
  "Lakshadweep Sea",
  "Palk Strait And Palk Bay",
  "Andaman Or Burma Sea",
  "India",
  "Gulf Of Mannar",
  "Gulf Of Kutch",
  "Gulf Of Khambhat",
  "Gulf Of Cambay",
];


const checkForIndiaRegion = (area) => {
  if(!area) return false;
  return IndiaRegionAreaStrings.some((indiaArea) =>
    area.trim().toLowerCase().includes(indiaArea.toLowerCase()),
  );
} 




const PinPopup = (address, bool= true) => {
  return { 
    popupHtml: `<div style="">${address}</div>`,
    popupOptions: {
      openPopup: bool,
    },
  };
};

/**
 *
 * Plugins values  "search", "route", "geocoder"
 *
 * */
const MapplsMap = forwardRef(({
  width = "100%",
  height = "400px",
  defaultLatLng,
  zoom = 14,
  setLatLngOnLoad = false,
  plugins = ["search"],
  coordinatesObserver = (v) => console.log("coordinates", v),
  onError = (e) => console.error("MapplsMap error:", e),
  sx = {},
}, ref) => {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const inputRef = useRef();
  const markerInstance = useRef(null);
  const [query, setQuery] = useState("");
  const [mapLoaded, setMapLoaded] = useState(false);

  const { coordinates, isFetching, error } = useGeolocation();

  const latLngRef = useRef(defaultLatLng?.lat && defaultLatLng?.lng ? defaultLatLng : coordinates);


  useImperativeHandle(ref, () => ({
    resetMappls: () => {
      setQuery("");
      if (markerInstance.current) {
        markerInstance.current.remove();
        markerInstance.current = null;
      }
    },
  }));


  const [
    fetchAddress,
    { data: addressData, error: addressError, isLoading: fetchingAddress },
  ] = useLazyGetReverseGeocodeMapplsQuery();

  // const [
  //   fetchAddressAnalytics,
  //   { data: addressAnalyticsData, error: addressAnalyticsError, isLoading: fetchingAddressAnalytics },
  // ] = useLazyGetAddressAnalyticsMapplsQuery();

  const addMarker = (lat, lng, options = {}, callback) => {
    if (markerInstance.current) {
      markerInstance.current.remove();
    }

    // Create new marker at clicked position
    markerInstance.current = createMarker(
      mapInstance.current,
      { lat, lng },
      options,
      callback,
    );
  };

  const handleOutOfIndiaError = (error) => {
    let err = {
      type: "OUT_OF_INDIA",
      skipFallback: true,
      error,
    };
    onError?.(err);

    // repan to correct location (India)
    if (mapInstance.current) {
      latLngRef.current = coordinates;
      mapInstance.current.setView(
        [latLngRef.current.lat, latLngRef.current.lng],
        17,
      );
      // fetch current location address and show in popup
      fetchAddress(coordinates)
        ?.unwrap()
        .then((res) => {
          const address =
            res?.data?.reverseGeocode?.results?.[0];
          const place= address?.formatted_address;
          const isAllowedIndiaRegion = checkForIndiaRegion(address?.area);
          if(!isAllowedIndiaRegion){
            onError?.({
              type: "CURRENT_LOCATION_OUT_OF_INDIA",
              message: ERROR_MESSAGES?.OUT_OF_INDIA,
              skipFallback: true,
            });
            return;
          }
          addMarker(
            latLngRef.current.lat,
            latLngRef.current.lng,
            PinPopup(place),
            coordinatesCallback,
          );
          coordinatesCallback(address)
        })
        .catch((err) => {
          console.error("Error fetching address for current location:", err);
        });
    }
  }

  const coordinatesCallback = async (cords) => {
    try {
      const crd = { lat: cords?.lat, lng: cords?.lng };
      const searchType = cords?.searchType;
      latLngRef.current = crd;
      const res = await fetchAddress(crd)?.unwrap();
      // const resAnalytics = await fetchAddressAnalytics(cords?.placeName+ ", " + cords?.placeAddress);
      // const standardizedAddress = res?.data.standardizedData;

      const notLocalitySearch =  searchType === "CITY" || searchType === "STATE";
      const address = res?.data?.reverseGeocode?.results?.[0];
      let result = { ...address };
      const isAllowedIndiaRegion = checkForIndiaRegion(result?.area);
      if (!isAllowedIndiaRegion) {
        handleOutOfIndiaError?.({
          message: ERROR_MESSAGES?.OUT_OF_INDIA,
          data: result,
          status: "OUT_OF_INDIA",
        });
        return;
      }
      if (cords?.placeName && cords?.placeAddress) {
        result = {
          ...result,
          placeName: cords.placeName,
          eloc: cords.eloc,
          placeAddress: cords.placeAddress,
          fullAddress: cords.placeName + ", " + cords.placeAddress,
        };

        if(notLocalitySearch){
          // override with more accruate details
          result.placeName= address?.locality || address?.street || cords.placeName;
          result.placeAddress= address?.formatted_address || cords.placeAddress;
          result.fullAddress= address?.formatted_address || (cords.placeName + ", " + cords.placeAddress);
        }
      }
      if (cords?.placeName === "Current Location" || notLocalitySearch) {
        setQuery(address?.formatted_address);
        addMarker(
          cords.lat,
          cords.lng,
          PinPopup(address?.formatted_address),
          coordinatesCallback,
        );
      }
      // console.log("log Fetching address for coordinates:", cords, result);
      coordinatesObserver(result);
    } catch (error) {
      console.error("Error fetching address:", error);
      if (error?.data?.status === "OUT_OF_INDIA") {
        handleOutOfIndiaError(error);
      } else {
        onError?.({
          type: "REVERSE_GEOCODE_FAILED",
          error,
          data: { cords },
        });
        coordinatesObserver(null, error);
      }
    }
  };

  function initMap() {
    try {
      if (window.mappls && mapContainer.current && !mapInstance.current) {
        mapInstance.current = new window.mappls.Map(mapContainer.current, {
          center: [latLngRef.current.lat, latLngRef.current.lng],
          zoom,
          location: true,
          fullscreenControl: false,
          maxBounds: Fitbounds,
        });
        mapInstance.current.on("error", (e) => {
          onError?.({ type: "MAP_RUNTIME_ERROR", skipFallback: true, error: e });
        });

        markerInstance.current = createMarker(
          mapInstance.current,
          { lat: latLngRef.current.lat, lng: latLngRef.current.lng },
          {},
          coordinatesCallback,
        );

        // Add click listener to map
        mapInstance.current.addListener("click", function (e) {
          latLngRef.current = e.lngLat;
          addMarker(e.lngLat.lat, e.lngLat.lng, coordinatesCallback);
          coordinatesCallback(latLngRef.current);
        });

        // search plugin initialization

        const inputElement = document.getElementById("auto");
        if (plugins.includes("search")) {
          mapInstance.current.addListener("load", function () {
            var optional_config = {
              region: "IND",
              hyperLocal: true,
              searchChars: 3,
            };

            new window.mappls.search(
              inputElement,
              optional_config,
              callback,
            );

            inputElement.click();

            function callback(data) {
              try {
                if (!data || !data?.[0]) {
                  throw new Error("No search results");
                }

                let dt = data[0];
                if (!dt) return false;
                const type = dt?.type;
                let eloc = dt.eLoc;
                let place = dt.placeName + ", " + dt.placeAddress;
                setQuery(dt.placeAddress ? place : dt.placeName);

                // Remove existing marker
                if (markerInstance.current) {
                  markerInstance.current.remove();
                }

                // Add marker using pinMarker
                window.mappls.pinMarker(
                  {
                    map: mapInstance.current,
                    pin: eloc,
                    ...PinPopup(place),
                  },
                  function (data) {
                    markerInstance.current = data;
                    latLngRef.current = data.obj.getPosition();
                    // markerInstance.current.fitbounds?.(Fitbounds);
                    markerInstance.current.fitmarker?.();
                    mapInstance.current.setView([latLngRef.current.lat, latLngRef.current.lng], 17.6);
                    coordinatesCallback({
                      ...latLngRef.current,
                      searchType: type,
                      eloc,
                      placeName: dt.placeName,
                      placeAddress: dt.placeAddress,
                    });
                  },
                );
              } catch (err) {
                onError?.({
                  type: "SEARCH_FAILED",
                  error: err,
                });
              }
            }
          });
        }

        // calling on first initialiaze
        setLatLngOnLoad && coordinatesCallback(latLngRef.current);
        setMapLoaded(true);
      }

    } catch (err) {
      onError?.({ type: "MAP_INIT_FAILED", error: err });
    }
  }

  useEffect(() => {
    if (!coordinates && !error) return;
    if (!latLngRef.current) latLngRef.current = coordinates;

    // Load Mappls script if not already present

    const existingScript = document.querySelector(
      `script[src*="sdk.mappls.com/map/sdk/web"]`,
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;

      script.onerror = (e) => {
        onError?.({
          type: "SDK_LOAD_FAILED",
          message: "Failed to load Mappls SDK",
          error: e,
        });
      };

      if (plugins?.length <= 0) {
        script.onload = () => {
          initMap(); // Initialize map only once loaded
        };
      } else {
        const pluginScript = document.createElement("script");
        pluginScript.src = pluginUrl;
        pluginScript.async = true;

        pluginScript.onerror = (err) => {
          onError?.({
            type: "PLUGIN_LOAD_FAILED",
            message: "Failed to load Mappls plugin",
            error: err,
          });
        };

        script.onload = () => {
          document.body.appendChild(pluginScript);
          pluginScript.onload = () => {
            initMap(); // Initialize map with plugins once loaded
          };
        };
      }

      document.body.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      // Cleanup: remove marker and map instance
      if (markerInstance.current) {
        markerInstance.current.remove();
        markerInstance.current = null;
      }
      if (mapInstance.current) {
        mapInstance.current = null;
      }
    };
  }, [coordinates, error, zoom]);



  useEffect(() => {
    if (mapLoaded && inputRef.current) {
      let time= setTimeout(() => {
        inputRef.current.click();
      }, 1000); // Ensure this runs after any potential rendering
      return () => clearTimeout(time);
    }
  }, [mapLoaded]);

  return (
    <>
      {isFetching ? (
        <div
          id="loader"
          style={{
            maxWidth: "100%",
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            ...sx,
          }}
        >
          <p>Maps loading...</p>
        </div>
      ) : (
        <div
          id="mappls-map"
          ref={mapContainer}
          style={{
            maxWidth: "100%",
            height,
            display: "flex",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            ...sx,
          }}
        >
          <input
            type="text"
            id="auto"
            ref={inputRef}
            autoFocus={true}
            onChange={(e) => setQuery(e.target.value)}
            name="auto"
            value={query}
            className=""
            placeholder="Search your Address"
            required=""
            style={{
              width: "80%",
              maxWidth: 500,
              position: "absolute",
              zIndex: 10,
              visibility: mapLoaded ? "visible" : "hidden",
              fontSize: 15,
              padding: 10,
              border: `1px solid ${WEB_COLORS.primary}`,
              outline: "none",
              top: 5,
              borderRadius: 10,
              marginY: 6,
              margin: 4,
            }}
            spellCheck="false"
          ></input>
          {!mapLoaded && (
            <div
              id="loader"
              style={{
                width,
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
              }}
            >
              <p>Maps loading...</p>
            </div>
          )}
        </div>
      )}
    </>
  );
});

export default MapplsMap;
